import { useCallback, useEffect, useMemo } from "react";
import { FEATURE_FLAGS, HubExchangeAccount, HubUserSignInResponseDto, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, getCexLabel, QueryKeys } from "@cede/utils";
import { CedeSDKError } from "@cede/utils";
import { WALLET_STATUS } from "@/constants/wallet";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useKatanaAuth } from "@/hooks/exchanges/katana/useKatanaAuth";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { invalidateReferralEligibilityQueries } from "@/hooks/referral/referralQueryKeys";
import { mixpanelClient } from "@/lib/mixpanel";
import { clearDirectEventTracking, trackEventDirect } from "@/lib/mixpanel/trackEvent";
import { ExchangeAccountFormData } from "@/store/accountStore";
import { useTrackWalletConnectStore } from "@/store/trackWalletConnectStore";
import { WALLET_SIGNER_STATUS, useWalletSignerStatusStore } from "@/store/walletSignerStatusStore";
import { formatAddressForDisplay } from "@/utils/address";
import { postExchangeAccount, deleteExchangeAccount } from "@/utils/api";
import { useLogin } from "@privy-io/react-auth";
import { useWallets } from "@privy-io/react-auth";
import * as Sentry from "@sentry/nextjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useSnackbar } from "notistack";
import { useAccount } from "wagmi";

type UseHubUserProps = {
  onAddExchangeAccountSuccess?: (account: HubExchangeAccount) => void;
  onAddExchangeAccountError?: (error: Error) => void;
  /** Errors the caller displays itself, so no snackbar repeats them. */
  isAddExchangeAccountErrorShownInline?: (error: Error) => boolean;
  onRemoveExchangeAccountSuccess?: () => void;
  onRemoveExchangeAccountError?: (error: Error) => void;
};

export const useHubUser = ({
  onAddExchangeAccountSuccess,
  onAddExchangeAccountError,
  isAddExchangeAccountErrorShownInline,
  onRemoveExchangeAccountSuccess,
  onRemoveExchangeAccountError,
}: UseHubUserProps = {}) => {
  const {
    identityToken,
    user,
    getAccessToken,
    logout: authProviderLogout,
    login: authProviderLogin,
    identityProvider,
    refreshIdentityToken,
    authenticated,
    ready,
  } = useAuthenticationProvider();

  const apiHelper = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { userInitiatedLogin, setUserInitiatedLogin, setIsNewUser } = useTrackWalletConnectStore();
  const [tonConnectUI] = useTonConnectUI();
  const { wallets: privyWallets, ready: arePrivyWalletsReady } = useWallets();
  const { address: wagmiAddress, status: wagmiStatus } = useAccount();
  const { isTMA } = useTelegramContext();
  const isTonOn = useFeatureFlagIsOn(FEATURE_FLAGS.TON_CONNECT_HUB);
  const queryClient = useQueryClient();
  const setMainEvmSignerStatus = useWalletSignerStatusStore((state) => state.setMainEvmSignerStatus);

  const { login: loginPrivy } = useLogin({
    onComplete: (props) => {
      setIsNewUser(props.isNewUser);
      // Clear error tracking on successful login so future errors can be tracked
      clearDirectEventTracking("privy-login-error");
    },
    onError: (error) => {
      // Track Privy login error with deduplication key to prevent multiple events
      trackEventDirect(
        METRICS_EVENTS.PRIVY_LOGIN_ERROR,
        {
          error: error.toString() || "Unknown error",
          identityProvider,
        },
        "privy-login-error", // Deduplication key
      );
    },
  });

  const login = useCallback(() => {
    setUserInitiatedLogin(true);

    // Use auth provider's login for local mode (opens LocalWalletModal)
    if (identityProvider === "local") {
      authProviderLogin();
      return;
    }

    if (!isTMA || !isTonOn) {
      loginPrivy();
    } else {
      const hasConnectedTon = mixpanelClient.getProperty("connectedTon");
      if (!hasConnectedTon) {
        setIsNewUser(true);
      }
      tonConnectUI.openModal();
    }
  }, [
    identityProvider,
    authProviderLogin,
    isTMA,
    isTonOn,
    loginPrivy,
    tonConnectUI,
    setIsNewUser,
    setUserInitiatedLogin,
  ]);

  const logout = useCallback(
    () =>
      authProviderLogout().then(() => {
        mixpanelClient.setPeople({
          lastLogoutDate: new Date().toISOString(),
        });
        mixpanelClient.reset();
        Sentry.setUser(null);
      }),
    [authProviderLogout],
  );

  const userAddress = user?.wallet?.address;

  // Auto signin query - runs ONLY when user actually initiated login (wallet connection)
  const signInQuery = useQuery({
    queryKey: ["signin", userAddress, identityProvider, authenticated],
    queryFn: async () => {
      // fix to ensure /me endpoint in the query "accounts" is called after the login
      // because we call login/signin only when the user actually connected his wallet
      let result: HubUserSignInResponseDto;
      if (userInitiatedLogin) {
        result = await apiHelper.postAuth(
          {
            identityToken,
            getAccessToken,
            identityProvider,
            refreshIdentityToken,
          },
          API_ENDPOINT.HUB_USER_SIGNIN,
          {},
        );

        // Reset the login flag after successful signin
        setUserInitiatedLogin(false);
      } else {
        // or just return the data from the query
        result = signInQuery.data as HubUserSignInResponseDto;
      }

      // Invalidate user queries so they can refetch after signin
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HUB_USER_ACCOUNTS] });

      return result;
    },
    // Only run when user actually initiated login AND has required auth data
    enabled: authenticated && userInitiatedLogin && !!userAddress && !!identityToken && !!identityProvider,

    // Retry logic with exponential backoff
    retry: (failureCount, error) => {
      // Don't retry more than 3 times
      if (failureCount >= 3) return false;

      // Don't retry auth errors (401, 403)
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) return false;
      }

      return true;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },

    // Caching strategy - don't persist signin state, only run when explicitly needed
    staleTime: 0, // Always considered stale
    gcTime: 0, // Don't keep in cache after completion
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on network reconnect - only on explicit wallet connection

    // Disable persistence for signin queries
    meta: {
      persist: false,
    },
  });

  const {
    data,
    isLoading: isLoadingHubUser,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.HUB_USER_ACCOUNTS, userAddress, identityProvider],
    enabled: !!userAddress && !!identityToken && !!identityProvider && (!userInitiatedLogin || signInQuery.isSuccess),
    queryFn: async () => {
      const defiAddress = user?.wallet?.address;
      if (!defiAddress) return;
      return apiHelper.getWithAuth(
        {
          identityToken,
          getAccessToken,
          identityProvider,
          refreshIdentityToken,
        },
        API_ENDPOINT.HUB_USER_ME,
      );
    },
    select: (data) => ({
      hubUser: data,
      userExchanges: Object.fromEntries(
        data?.exchangeAccounts?.map((exchange) => [exchange.exchangeId, exchange]) ?? [],
      ),
    }),
  });

  const { hubUser, userExchanges } = data ?? { hubUser: undefined, userExchanges: {} };

  // Fetch NFT images from metadata URIs, memoized by defiAddress
  // TODO : might be better to fetch metadata in the backend and not fetch for eahc nft but by type only
  const nftBalances = hubUser?.nftBalances ?? [];
  const metadataUris = useMemo(() => nftBalances.map((nft) => nft.metadataUri), [hubUser?.defiAddress]);

  const { data: nftImages = [], isLoading: isLoadingNftImages } = useQuery({
    queryKey: ["nftImages", hubUser?.defiAddress],
    queryFn: async () => {
      if (metadataUris.length === 0) return [];

      const results = await Promise.allSettled(
        metadataUris.map(async (uri) => {
          const response = await fetch(uri);
          if (!response.ok) throw new Error(`Failed to fetch ${uri}`);
          const metadata = await response.json();
          return metadata.image as string;
        }),
      );

      return results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === "fulfilled")
        .map((result) => result.value);
    },
    enabled: !!hubUser?.defiAddress && metadataUris.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const { mutateAsync: addStandardExchangeAccount, isPending: addStandardExchangeAccountIsLoading } = useMutation({
    mutationFn: async (formData: ExchangeAccountFormData) => {
      // Only include uid and password in the payload if they have actual values
      const { uid, password, ...baseFormData } = formData;
      const payload = {
        ...baseFormData,
        ...(uid && uid.trim() !== "" && { uid }),
        ...(password && password.trim() !== "" && { password }),
      } as ExchangeAccountFormData;

      const exchangeAccount = (await postExchangeAccount(
        { identityToken, getAccessToken, identityProvider, refreshIdentityToken },
        payload,
      )) as HubExchangeAccount; // safe casting because POST and DELETE use same paths
      await refetch();
      onAddExchangeAccountSuccess?.(exchangeAccount);
      const cexLabel = getCexLabel(exchangeAccount.exchangeId);
      enqueueSnackbar(`Exchange account for ${cexLabel} was added successfully`, { variant: "success" });
      await invalidateReferralEligibilityQueries(queryClient);
    },
    onError: (error) => {
      if (!isAddExchangeAccountErrorShownInline?.(error)) {
        enqueueSnackbar(
          error instanceof CedeSDKError
            ? error.message
            : `Failed to add exchange account: ${error instanceof Error ? error.message : "Unknown error"}`,
          { variant: "error" },
        );
      }
      onAddExchangeAccountError?.(error as Error);
    },
  });

  const { connectKatanaAccount, connectKatanaAccountIsLoading } = useKatanaAuth({
    defiAddress: hubUser?.defiAddress ?? undefined,
    onSuccess: async (accountId) => {
      const refetched = await refetch();
      const persistedAccount = refetched.data?.hubUser?.exchangeAccounts?.find(
        (account: HubExchangeAccount) => account.id === accountId,
      );
      if (persistedAccount) {
        onAddExchangeAccountSuccess?.(persistedAccount);
      }
      await invalidateReferralEligibilityQueries(queryClient);
    },
    onError: onAddExchangeAccountError,
  });

  /**
   * Unified entry point for connecting an exchange account. Katana goes
   * through the two-phase delegated-key flow internally (prepare + EIP-712
   * sign + authorize); every other exchange takes the generic HMAC paste
   * path. Callers only need to populate `ExchangeAccountFormData` and let
   * the hook pick the right flow.
   */
  const addExchangeAccount = useCallback(
    (formData: ExchangeAccountFormData) => {
      if (formData.selectedCex === "katana") {
        return connectKatanaAccount({ apiKey: formData.apiKey, apiSecret: formData.secretKey });
      }
      return addStandardExchangeAccount(formData);
    },
    [addStandardExchangeAccount, connectKatanaAccount],
  );

  const addExchangeAccountIsLoading = addStandardExchangeAccountIsLoading || connectKatanaAccountIsLoading;

  const { mutateAsync: removeExchangeAccount, isPending: removeExchangeAccountIsLoading } = useMutation({
    mutationFn: async ({ accountId, exchangeId }: { accountId: string; exchangeId: string }) => {
      await deleteExchangeAccount({ identityToken, getAccessToken, identityProvider, refreshIdentityToken }, accountId);
      await refetch();
      onRemoveExchangeAccountSuccess?.();

      enqueueSnackbar(
        `Your account has been successfully unlinked. We recommend deleting your API key from the ${getCexLabel(
          exchangeId,
        )} interface.`,
        { variant: "success" },
      );
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to remove exchange account";
      enqueueSnackbar(message, { variant: "error" });
      onRemoveExchangeAccountError?.(error as Error);
    },
  });

  const mainWalletAddress = formatAddressForDisplay(
    user?.wallet?.address || hubUser?.defiAddress || "",
    identityProvider,
  );
  const userFriendlyDefiAddress = formatAddressForDisplay(hubUser?.defiAddress || "", identityProvider);
  const authenticatedWalletAddress = user?.wallet?.address?.toLowerCase();
  const hasConnectedAuthenticatedPrivyWallet =
    identityProvider === "privy" && authenticatedWalletAddress
      ? privyWallets.some((wallet) => wallet.address.toLowerCase() === authenticatedWalletAddress)
      : false;

  // Computed wallet connection status for trading widgets
  // Returns: "initializing" | "disconnected" | "connecting" | "connected"
  const walletStatus = useMemo(() => {
    if (!ready) return WALLET_STATUS.INITIALIZING;
    if (!authenticated) return WALLET_STATUS.DISCONNECTED;

    if (identityProvider === "privy") {
      if (!arePrivyWalletsReady || wagmiStatus === "connecting" || wagmiStatus === "reconnecting") {
        return WALLET_STATUS.CONNECTING;
      }

      if (!authenticatedWalletAddress || !hasConnectedAuthenticatedPrivyWallet) {
        return WALLET_STATUS.DISCONNECTED;
      }

      // wagmi reports disconnected — the connector dropped (phone sleep, extension restart)
      if (wagmiStatus === "disconnected") {
        return WALLET_STATUS.DISCONNECTED;
      }

      if (!wagmiAddress || wagmiAddress.toLowerCase() !== authenticatedWalletAddress) {
        return WALLET_STATUS.CONNECTING;
      }
    }

    if (isLoadingHubUser || !hubUser?.defiAddress) return WALLET_STATUS.CONNECTING;
    return WALLET_STATUS.CONNECTED;
  }, [
    ready,
    authenticated,
    identityProvider,
    arePrivyWalletsReady,
    wagmiStatus,
    authenticatedWalletAddress,
    hasConnectedAuthenticatedPrivyWallet,
    wagmiAddress,
    isLoadingHubUser,
    hubUser?.defiAddress,
  ]);

  const auth = useMemo(
    () => ({
      identityToken,
      getAccessToken,
      identityProvider,
      refreshIdentityToken,
    }),
    [identityToken, getAccessToken, identityProvider, refreshIdentityToken],
  );

  useEffect(() => {
    if (!authenticated || walletStatus !== WALLET_STATUS.CONNECTED) {
      setMainEvmSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
    }
  }, [authenticated, setMainEvmSignerStatus, walletStatus]);

  const isAuthStateStable = walletStatus === WALLET_STATUS.CONNECTED || walletStatus === WALLET_STATUS.DISCONNECTED;

  return {
    login,
    hubUser,
    mainWalletAddress,
    userFriendlyDefiAddress,
    isLoadingHubUser,
    refetchHubUser: refetch,
    addExchangeAccount,
    addExchangeAccountIsLoading,
    identityProvider,
    identityToken,
    getAccessToken,
    logout,
    removeExchangeAccount,
    removeExchangeAccountIsLoading,
    auth,
    authenticated,
    ready,
    walletStatus,
    isAuthStateStable,
    userExchanges,
    nftImages,
    isLoadingNftImages,
  };
};
